import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useRegisterSW } from "virtual:pwa-register/react";
import useUserAgent from "@/lib/hooks/useUserAgent";
import { Button } from "@/components/ui/button";
import { DefaultButton } from "@/components/DefaultButton";

const UPDATE_TOAST_ID = "toast:pwa-update";
const UPDATE_CHECK_INTERVAL_MS = 1 * 60 * 1000; // 3 min;

export default function PwaUpdateToast() {
  const { isStandalone } = useUserAgent();
  const isStandaloneRef = useRef<boolean | null>(null);
  const intervalRef = useRef<number | null>(null);
  const updatingTimerRef = useRef<number | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    isStandaloneRef.current = isStandalone;
  }, [isStandalone]);

  const onRegistered = useCallback(
    (registration?: ServiceWorkerRegistration) => {
      if (!registration) return;

      const checkForUpdates = () => {
        if (isStandaloneRef.current === true) {
          registration.update().catch(() => undefined);
        }
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          checkForUpdates();
        }
      };

      const handleFocus = () => {
        checkForUpdates();
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("focus", handleFocus);

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);

      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }

      intervalRef.current = window.setInterval(() => {
        checkForUpdates();
      }, UPDATE_CHECK_INTERVAL_MS);

      checkForUpdates();
    },
    [],
  );

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered,
  });

  useEffect(() => {
    if (isStandalone !== true) {
      toast.dismiss(UPDATE_TOAST_ID);
      setNeedRefresh(false);
      setIsUpdating(false);
      return;
    }

    if (!needRefresh) {
      toast.dismiss(UPDATE_TOAST_ID);
      setIsUpdating(false);
      return;
    }

    const close = () => {
      setNeedRefresh(false);
      setIsUpdating(false);
      toast.dismiss(UPDATE_TOAST_ID);
    };

    toast.custom(
      () => (
        <div className="relative p-3 flex flex-col items-start">
          {/*<img
            src="https://cdn.navalabs.io/icons/valentino-head_fh2q2k.png"
            alt="Varentino"
            className="rounded-md w-14 shadow-sm mt-1"
          />*/}
          <Button
            variant="link"
            className="absolute top-0 right-3 text-lg font-bold text-zinc-300 text-nowrap h-8 px-2 -mr-2 hover:no-underline"
            aria-label="Dismiss update notification"
            onClick={() => toast.dismiss(UPDATE_TOAST_ID)}
          >
            x
          </Button>
          <div className="flex-1 text-white ml-0.5">
            <p className="font-extrabold text-shadow-black-sm text-base uppercase">
              Update Available!
            </p>
          </div>
          <div className="flex justify-between items-center gap-0">
            <p className="font-bold text-xs text-emerald-50">
              A newer version is ready. Update to get the latest features and
              fixes.
            </p>
            <DefaultButton
              variant="green"
              className="h-8 pt-0! text-sm font-bold text-nowrap hover:no-underline"
              onClick={() => {
                if (isUpdating) return;
                setIsUpdating(true);
                setNeedRefresh(false);
                updateServiceWorker(true);

                if (updatingTimerRef.current) {
                  window.clearTimeout(updatingTimerRef.current);
                }

                updatingTimerRef.current = window.setTimeout(() => {
                  setIsUpdating(false);
                }, 10000);
              }}
              disabled={isUpdating}
            >
              <span className="-mt-1 uppercase">
                {isUpdating ? "Updating..." : "Update"}
              </span>
            </DefaultButton>
          </div>
        </div>
      ),
      {
        id: UPDATE_TOAST_ID,
        position: "bottom-center",
        duration: Infinity,
      },
    );
  }, [
    needRefresh,
    isStandalone,
    isUpdating,
    setNeedRefresh,
    updateServiceWorker,
  ]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
      if (updatingTimerRef.current) {
        window.clearTimeout(updatingTimerRef.current);
      }
    };
  }, []);

  return null;
}
